import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouteContext } from '@tanstack/react-router';
import { Alert, Button, message as antdMessage } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  useCreateBillingCheckoutSession,
  getGetBillingOverviewQueryKey,
  useCreateBillingPortalSession,
} from '../../../api/generated/api';
import { formatDate } from '../../../ui/utils/format-date';
import { usePermissions } from '../../account/hooks';
import { linkPayticsStripeCustomer } from '../../paytics';
import { useRefreshBillingContext } from '../hooks/useRefreshBillingContext';
import { BILLING_GATE_STRATEGY } from '../shared/gate-strategy';

import { NotifyAdminButton } from './NotifyAdminButton';

import type { BillingBlockedEventDetail } from '../../../api/axios.instance';

type BannerState = { code: string | undefined };

/**
 * Listens for billing-blocked 402 responses from non-chat axios calls and pins a
 * top-of-app banner with copy and an action per error code — warning or error for
 * a manager who can act, plain info for a rep who can't. This intentionally avoids
 * modals so users can keep navigating; they'll only hit the same block if they
 * retry the exact action. Dismissible; re-appears on new 402s with a different code.
 */
export const BillingUpgradeBanner = () => {
  const skipBillingGate = import.meta.env.VITE_SKIP_BILLING_GATE === 'true';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedOrganization, selectedMembership } = useRouteContext({
    from: '/_authenticated/_app',
  });
  const organizationId = selectedOrganization.id;
  const renewsAt = selectedMembership.organization.credits?.renewsAt ?? null;
  const trial = selectedMembership.organization.credits?.trial ?? false;
  /** Out of credits is already on the billing bar; this banner covers the "not enough" 402s. */
  const exhausted = selectedMembership.organization.credits?.exhausted ?? false;
  const { can } = usePermissions();
  const canManageBilling = can('settings', 'manageBilling');
  const checkoutMutation = useCreateBillingCheckoutSession();
  const portalMutation = useCreateBillingPortalSession();
  const refreshBillingContext = useRefreshBillingContext();

  const [state, setState] = useState<BannerState | null>(null);
  /** Tracks the last seen code so a repeat event doesn't keep re-opening a dismissed banner. */
  const dismissedCodesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<BillingBlockedEventDetail>).detail ?? {};
      const code = detail.code ?? 'BILLING_BLOCKED';
      if (code === 'NO_SUBSCRIPTION' && BILLING_GATE_STRATEGY === 'in_app') return;
      if (dismissedCodesRef.current.has(code)) return;
      setState({ code });
    };
    window.addEventListener('billing:upgradeRequired', handler);
    return () => window.removeEventListener('billing:upgradeRequired', handler);
  }, []);

  /**
   * When an upgrade resolves (UpgradeModal dispatches `billing:upgradeResolved`) or credits land
   * (the top-up return page dispatches `billing:creditsAdded`), clear any active banner and forget
   * previously dismissed codes so the next 402 — if one ever happens — is evaluated fresh rather
   * than silently suppressed.
   */
  useEffect(() => {
    const handler = () => {
      dismissedCodesRef.current = new Set();
      setState(null);
    };
    window.addEventListener('billing:upgradeResolved', handler);
    window.addEventListener('billing:creditsAdded', handler);
    return () => {
      window.removeEventListener('billing:upgradeResolved', handler);
      window.removeEventListener('billing:creditsAdded', handler);
    };
  }, []);

  /**
   * The banner is mounted for the whole app, so it also owns the refresh on `billing:creditsAdded`:
   * the account carries the credit context (drawer widget, billing bar) and would otherwise keep
   * showing the balance from before the purchase.
   */
  useEffect(() => {
    const handler = () => {
      void refreshBillingContext(organizationId);
    };
    window.addEventListener('billing:creditsAdded', handler);
    return () => window.removeEventListener('billing:creditsAdded', handler);
  }, [refreshBillingContext, organizationId]);

  const close = useCallback(() => {
    if (state?.code) dismissedCodesRef.current.add(state.code);
    setState(null);
  }, [state]);

  /**
   * Open the Stripe Customer Portal (same flow as the Manage plan button on the
   * billing settings page). Used for the `portal` actions (payment failed,
   * cancellation pending) and the `NO_SUBSCRIPTION` checkout. The `billing` and
   * `upgrade` actions navigate to `/settings/billing` instead — if the banner is
   * already showing there, the click is a no-op beyond dismissing the banner,
   * which is fine.
   */
  const handleOpenBilling = async () => {
    try {
      let url: string | null;
      if (state?.code === 'NO_SUBSCRIPTION') {
        const { data } = await checkoutMutation.mutateAsync({ organizationId, data: {} });
        // Leaving for Stripe's hosted page would cancel the Paytics mapping still in flight.
        await linkPayticsStripeCustomer(data.stripeCustomerId);
        url = data.url;
      } else {
        url = (await portalMutation.mutateAsync({ organizationId })).data.url;
      }
      void queryClient.invalidateQueries({
        queryKey: getGetBillingOverviewQueryKey(organizationId),
      });
      if (url) {
        window.location.href = url;
      }
    } catch {
      antdMessage.error('Failed to open billing. Please try again.');
    }
  };

  if (skipBillingGate) return null;

  if (!state) return null;
  if (state.code === 'INSUFFICIENT_CREDITS' && exhausted) return null;

  const isNoSubscription = state.code === 'NO_SUBSCRIPTION';
  const isLoading = checkoutMutation.isPending || portalMutation.isPending;

  if (isNoSubscription) {
    if (!canManageBilling) {
      return (
        <Alert
          banner
          type="info"
          showIcon
          closable={{ onClose: close }}
          title="Your company needs an active subscription. Ask your admin to start the plan."
        />
      );
    }

    return (
      <Alert
        banner
        type="info"
        showIcon
        closable={{ onClose: close }}
        title="Start your subscription to unlock all features."
        action={
          <Button
            size="small"
            type="primary"
            loading={isLoading}
            onClick={async () => {
              await handleOpenBilling();
              close();
            }}
          >
            Start trial
          </Button>
        }
      />
    );
  }

  const renewal = renewsAt ? `until ${formatDate(renewsAt)}` : 'until your plan renews';
  const copy: Record<
    string,
    { manager: string; rep: string; action: 'billing' | 'buy' | 'portal' | 'upgrade' | null; label: string }
  > = {
    INSUFFICIENT_CREDITS: {
      manager: `Out of credits ${renewal}.`,
      rep: `Your company is out of credits ${renewal}. Ask your billing admin.`,
      action: trial ? 'upgrade' : 'buy',
      label: trial ? 'Upgrade now' : 'Buy credits',
    },
    PAYMENT_FAILED: {
      manager: 'Your last payment failed — update your card.',
      rep: "Your company's last payment failed. Ask your billing admin to update the card.",
      action: 'portal',
      label: 'Update card',
    },
    SUBSCRIPTION_INACTIVE: {
      manager: 'Your plan ended. Re-subscribe to use your remaining credits.',
      rep: "Your company's plan ended. Ask your billing admin to re-subscribe.",
      action: 'billing',
      label: 'Re-subscribe',
    },
    CANCELLATION_PENDING: {
      manager: 'Auto-recharge is off while your plan is scheduled to end. Reactivate the plan in the billing portal, or buy a one-time top-up.',
      rep: "Your company's plan is scheduled to end. Ask your billing admin.",
      action: 'portal',
      label: 'Manage subscription',
    },
  };
  const entry = copy[state.code ?? ''] ?? {
    manager: 'This action is not available on your plan right now.',
    rep: 'This action is not available on your plan right now. Ask your billing admin.',
    action: 'billing' as const,
    label: 'Open billing',
  };

  if (!canManageBilling) {
    return (
      <Alert
        banner
        type="info"
        showIcon
        closable={{ onClose: close }}
        title={entry.rep}
        action={
          state.code === 'INSUFFICIENT_CREDITS' ? <NotifyAdminButton organizationId={organizationId} /> : undefined
        }
      />
    );
  }

  return (
    <Alert
      banner
      type={state.code === 'PAYMENT_FAILED' ? 'error' : 'warning'}
      showIcon
      closable={{ onClose: close }}
      title={entry.manager}
      action={
        <Button
          size="small"
          type="primary"
          loading={isLoading}
          onClick={async () => {
            if (entry.action === 'portal') {
              await handleOpenBilling();
            } else if (entry.action === 'upgrade') {
              void navigate({ to: '/settings/billing', search: { upgrade: 1 } });
            } else if (entry.action === 'buy') {
              void navigate({ to: '/settings/billing', search: { buy: 1 } });
            } else {
              void navigate({ to: '/settings/billing' });
            }
            close();
          }}
        >
          {entry.label}
        </Button>
      }
    />
  );
};
